import type { ClassificationResult } from '@classification-testing/domain';
import { ContentType } from '@classification-testing/domain';
import { getContentTypeIcon } from '@classification-testing/utils';
import { formatTimestamp } from '@core/utils';
import { IconButton, Eye, IconButtonSize, IconSize, StatusCircleBlock, Icon, Badge, Dialog } from '@soteria/design-system-ve';

import * as styles from './ClassificationResultDialog.css';
import { ContentDisplaySection, ImageDisplaySection } from './ClassificationResultDialogContentSections';
import { PoliciesSection } from './PolicyComponents';

// Type for classification label (from ClassificationResult interface)
type ClassificationLabel = ClassificationResult['labels'][0];

interface ClassificationResultDialogProps {
  result: ClassificationResult;
  isOpen: boolean;
  onClose: () => void;
  isSafe: boolean;
  isContentBlurred: boolean;
  onToggleBlur: () => void;
}

// Helper function to get dialog title based on content type
function getDialogTitle(contentType: ContentType) {
  switch (contentType) {
    case ContentType.AUDIO:
      return 'Audio Classification Result';
    case ContentType.IMAGE:
      return 'Image Classification Result';
    case ContentType.TEXT:
      return 'Text Classification Result';
    case ContentType.MIXED:
      return 'Mixed Content Classification Result';
    default:
      return 'Classification Result';
  }
}

// Label info component
function LabelInfo({ label }: { label: ClassificationLabel }) {
  return (
    <div className={styles.categoryInfo}>
      <span className={styles.categoryName}>{label.label_name}</span>
      {label.label_short_name && label.label_short_name !== label.label_name && <span className={styles.categoryShortName}>({label.label_short_name})</span>}
    </div>
  );
}

// Helper function to get scope display text
function getScopeDisplayText(scope: ClassificationLabel['scope']): string {
  if (!scope) return '';

  return scope.global ? 'Global' : `Communities: ${scope.communities?.length || 0}`;
}

// Label details component
function LabelDetails({ label, contentType }: { label: ClassificationLabel; contentType: ContentType }) {
  const showSeverity = contentType !== ContentType.IMAGE && label.severity !== undefined;
  // Only show score for IMAGE content types
  const showScore = contentType === ContentType.IMAGE && label.score !== undefined;

  return (
    <div className={styles.categoryDetails}>
      {/* Hide severity for image labels */}
      {showSeverity && (
        <div className={styles.categoryMetric}>
          <span className={styles.categoryLabel}>Severity:</span>
          <span className={styles.severityValue}>{label.severity}</span>
        </div>
      )}
      {/* Show "Score:" for images only (not for text) */}
      {showScore && label.score !== undefined && (
        <div className={styles.categoryMetric}>
          <span className={styles.categoryLabel}>Score:</span>
          <span className={styles.severityValue}>{label.score.toFixed(4)}%</span>
        </div>
      )}
      {label.scope && (
        <div className={styles.categoryMetric}>
          <span className={styles.categoryLabel}>Scope:</span>
          <span className={styles.severityValue}>{getScopeDisplayText(label.scope)}</span>
        </div>
      )}
      {label.metadata && (
        <div className={styles.categoryMetric}>
          <span className={styles.categoryLabel}>Policy:</span>
          <span className={styles.severityValue}>{label.metadata.policy}</span>
        </div>
      )}
    </div>
  );
}

// Label item component to reduce complexity
function LabelItem({ label, contentType }: { label: ClassificationLabel; contentType: ContentType }) {
  const isSafe = label.label_name.toLowerCase().includes('safe');

  return (
    <div className={`${styles.categoryItem} ${isSafe ? styles.categoryItemSafe : styles.categoryItemUnsafe}`}>
      <LabelInfo label={label} />
      <LabelDetails contentType={contentType} label={label} />
    </div>
  );
}

// Enhanced Labels section component
function LabelsSection({ result }: { result: ClassificationResult }) {
  if (!result.labels || result.labels.length === 0) {
    return null;
  }

  return (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>Labels</h3>
      <div className={styles.categoriesList}>
        {result.labels.map(label => (
          <LabelItem key={label.label_id || label.label_name} contentType={result.contentType} label={label} />
        ))}
      </div>
    </div>
  );
}

// Categories section component
function CategoriesSection({ result }: { result: ClassificationResult }) {
  if (!result.categories || result.categories.length === 0) {
    return null;
  }

  return (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>Categories</h3>
      <div className={styles.categoriesList}>
        {result.categories.map(category => (
          <div key={category} className={styles.categoryItem}>
            <div className={styles.categoryInfo}>
              <span className={styles.categoryName}>{category}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Enhanced metadata section component
function EnhancedMetadataSection({ result }: { result: ClassificationResult }) {
  const hasEnhancedData = result.confidenceScore !== undefined || result.languageDetected || result.duration;

  if (!hasEnhancedData) {
    return null;
  }

  // For IMAGE types, score is already 0-100, for others it's 0-1 (needs * 100)
  const getScoreValue = (): number => {
    if (result.confidenceScore === undefined) return 0;

    return result.contentType === ContentType.IMAGE ? result.confidenceScore : result.confidenceScore * 100;
  };

  return (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>Classification Metadata</h3>
      <div className={styles.metadataGrid}>
        {result.confidenceScore !== undefined && (
          <div className={styles.metadataItemInline}>
            <span className={styles.metadataLabel}>{result.contentType === ContentType.IMAGE ? 'Overall Score:' : 'Overall Confidence:'}</span>
            <span className={styles.metadataValue}>{getScoreValue().toFixed(4)}%</span>
          </div>
        )}
        {result.languageDetected && (
          <div className={styles.metadataItemInline}>
            <span className={styles.metadataLabel}>Language Detected:</span>
            <span className={styles.metadataValue}>{result.languageDetected.toUpperCase()}</span>
          </div>
        )}
        {result.duration && (
          <div className={styles.metadataItemInline}>
            <span className={styles.metadataLabel}>Duration:</span>
            <span className={styles.metadataValue}>{(result.duration / 1000).toFixed(2)}s</span>
          </div>
        )}
      </div>
    </div>
  );
}

// Explanation section component
function ExplanationSection({ result }: { result: ClassificationResult }) {
  if (!result.explanation) {
    return null;
  }

  return (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>Explanation</h3>
      <div className={styles.contentBox}>
        <span>{result.explanation}</span>
      </div>
    </div>
  );
}

/**
 * Dialog component for displaying classification result details
 */
export function ClassificationResultDialog({ result, isOpen, onClose, isSafe, isContentBlurred, onToggleBlur }: ClassificationResultDialogProps) {
  const dialogTitle = getDialogTitle(result.contentType);
  const dialogDescription = formatTimestamp(result.timestamp);

  const customHeader = (
    <div>
      <div className={styles.titleContainer}>
        <Icon icon={getContentTypeIcon(result.contentType)} iconSize={IconSize.Small} />
        <h2 className={styles.dialogTitle}>{dialogTitle}</h2>
        {!isSafe && (
          <IconButton
            buttonSize={IconButtonSize.Small}
            icon={isContentBlurred ? Eye : StatusCircleBlock}
            iconSize={IconSize.Small}
            title={isContentBlurred ? 'Show content' : 'Hide content'}
            onClick={onToggleBlur}
          />
        )}
      </div>
      {dialogDescription && <p className={styles.dialogDescription}>{dialogDescription}</p>}
    </div>
  );

  return (
    <Dialog cancelAction={{ label: 'Close' }} customHeader={customHeader} isOpen={isOpen} size="xl" onClose={onClose}>
      <div className={styles.statusBadge}>
        <Badge className={isSafe ? styles.safeBadge : styles.unsafeBadge} text={isSafe ? 'SAFE' : 'UNSAFE'} />
      </div>
      <div className={styles.container}>
        {/* Image Display */}
        <ImageDisplaySection isContentBlurred={isContentBlurred} isSafe={isSafe} result={result} onToggleBlur={onToggleBlur} />

        {/* Content Display */}
        <ContentDisplaySection isContentBlurred={isContentBlurred} isSafe={isSafe} result={result} onToggleBlur={onToggleBlur} />

        {/* Classification Explanation */}
        <ExplanationSection result={result} />

        {/* Enhanced Metadata */}
        <EnhancedMetadataSection result={result} />

        {/* Categories */}
        <CategoriesSection result={result} />

        {/* Labels */}
        <LabelsSection result={result} />

        {/* Policies */}
        <PoliciesSection result={result} />
      </div>
    </Dialog>
  );
}
